import { makeAutoObservable } from "mobx";
import { toast } from "../components/ui/toast";
import i18n from "../i18n";
import { DIR, Dir, LANG, Lang, LANG_KEY } from "../types";

export enum SidebarState {
  EXPANDED = "expanded",
  COLLAPSED = "collapsed",
  ICONS_ONLY = "icons_only",
}

class GlobalStore {
  // Language and Direction
  lang: Lang = LANG.en;
  dir: Dir = DIR.ltr;

  // Sidebar State
  sidebarState: SidebarState = SidebarState.EXPANDED;
  isMobileView: boolean = false;

  // Conversation Notifications
  conversationNotifications: Set<string> = new Set(); // conversation IDs with unread messages

  constructor() {
    makeAutoObservable(this);
    this.initializeState();
    this.handleResize();
    // Bind the event handler to maintain 'this' context
    window.addEventListener("resize", this.handleResize.bind(this));
  }

  initializeState() {
    // Load language from localStorage
    const savedLang = localStorage.getItem(LANG_KEY);
    if (savedLang && (savedLang === LANG.en || savedLang === LANG.ar)) {
      this.lang = savedLang as Lang;
      this.dir = savedLang === LANG.ar ? DIR.rtl : DIR.ltr;
    }

    // Load sidebar state from localStorage
    const savedState = localStorage.getItem("sidebarState");
    if (
      savedState &&
      Object.values(SidebarState).includes(savedState as SidebarState)
    ) {
      this.sidebarState = savedState as SidebarState;
    }

    // Check if mobile view
    this.isMobileView = window.innerWidth < 768; // md breakpoint
  }

  // Use arrow function to bind 'this' automatically
  handleResize = () => {
    const width = window.innerWidth;
    const wasMobile = this.isMobileView;
    const newIsMobile = width < 768;

    this.isMobileView = newIsMobile;

    console.log("RESIZE:", { width, isMobileView: this.isMobileView, wasMobile, currentState: this.sidebarState });

    // Only auto-collapse when switching from desktop to mobile AND the sidebar is expanded/icons-only
    if (!wasMobile && this.isMobileView && this.sidebarState !== SidebarState.COLLAPSED) {
      console.log("Switched to mobile - collapsing sidebar");
      this.sidebarState = SidebarState.COLLAPSED;
    }

    // Only auto-expand when switching from mobile to desktop and sidebar was collapsed
    if (wasMobile && !this.isMobileView && this.sidebarState === SidebarState.COLLAPSED) {
      console.log("Switched to desktop - restoring sidebar state");
      // Restore from localStorage or use EXPANDED
      const savedState = localStorage.getItem("sidebarState");
      if (savedState && Object.values(SidebarState).includes(savedState as SidebarState)) {
        this.sidebarState = savedState as SidebarState;
      } else {
        this.sidebarState = SidebarState.EXPANDED;
      }
    }
  };

  setLang = async (lang: Lang) => {
    this.lang = lang;
    this.dir = lang === LANG.ar ? DIR.rtl : DIR.ltr;

    // Update i18n language
    await i18n.changeLanguage(lang);

    // Update DOM attributes
    document.documentElement.setAttribute(LANG_KEY, lang);
    document.documentElement.setAttribute("dir", this.dir);
    document.documentElement.style.setProperty("--direction", this.dir);

    // Save to localStorage
    localStorage.setItem(LANG_KEY, lang);
  };

  // Alias for setLang for backward compatibility
  changeLang = (lang: Lang) => {
    this.setLang(lang);
  };

  setSidebarState = (state: SidebarState) => {
    console.log("setSidebarState called:", state);
    this.sidebarState = state;
    localStorage.setItem("sidebarState", state);
  };

  // FIX: Use arrow function to bind 'this'
  toggleSidebar = () => {
    console.log("toggleSidebar called, current state:", this.sidebarState, "isMobileView:", this.isMobileView);

    if (this.isMobileView) {
      // On mobile, toggle between COLLAPSED and EXPANDED
      const newState = this.sidebarState === SidebarState.COLLAPSED
        ? SidebarState.EXPANDED
        : SidebarState.COLLAPSED;
      console.log("Mobile toggle - setting to:", newState);
      this.sidebarState = newState;
    } else {
      // On desktop, cycle through states
      if (this.sidebarState === SidebarState.EXPANDED) {
        this.sidebarState = SidebarState.ICONS_ONLY;
      } else if (this.sidebarState === SidebarState.ICONS_ONLY) {
        this.sidebarState = SidebarState.COLLAPSED;
      } else {
        this.sidebarState = SidebarState.EXPANDED;
      }
      console.log("Desktop toggle - new state:", this.sidebarState);
    }
  };

  // Legacy support for existing code
  get isMenuToggled() {
    return this.sidebarState === SidebarState.COLLAPSED;
  }

  // Legacy method for backward compatibility
  setMenuToggled = (toggled: boolean) => {
    this.setSidebarState(
      toggled ? SidebarState.COLLAPSED : SidebarState.EXPANDED,
    );
  };

  // Show success message
  showSuccess = (messageText: string) => {
    toast.success(messageText);
  };

  // Show error message
  showError = (messageText: string) => {
    toast.error(messageText);
  };

  // Show warning message
  showWarning = (messageText: string) => {
    toast.warning(messageText);
  };

  // Show info message
  showInfo = (messageText: string) => {
    toast.info(messageText);
  };

  // Add conversation notification
  addConversationNotification = (conversationId: string) => {
    this.conversationNotifications.add(conversationId);

    // Auto-expand sidebar when notification arrives (unless user is in mobile collapsed view)
    if (this.sidebarState === SidebarState.ICONS_ONLY) {
      this.setSidebarState(SidebarState.EXPANDED);
    } else if (
      this.sidebarState === SidebarState.COLLAPSED &&
      !this.isMobileView
    ) {
      this.setSidebarState(SidebarState.EXPANDED);
    }
  };

  // Remove conversation notification (called when user views the conversation)
  removeConversationNotification = (conversationId: string) => {
    this.conversationNotifications.delete(conversationId);
  };

  // Check if a conversation has notifications
  hasConversationNotification = (conversationId: string): boolean => {
    return this.conversationNotifications.has(conversationId);
  };

  // Clear all notifications
  clearAllNotifications = () => {
    this.conversationNotifications.clear();
  };
}

export default GlobalStore;